import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import { get, patch as patchRequest, deleteRequest } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import thumbPlaceholder from "../../../assets/images/user-dashboard/image.svg";
import {
  ActivityDotIcon,
  CalendarIcon,
  DocumentIcon,
  DollarIcon,
  GiftIcon,
  PercentIcon,
  SpendIcon,
} from "../../../components/Common/SettlementIcons";
import { extractErrorMessage } from "../../../utils/errorHandler";

/* -------------------------------------------------------------------------
 * AdminRestaurantDetailPage — "Restaurant Detail" admin view.
 *
 * Sections wired to real data (adminSingleRestaurant + adminSettlementsDashboard):
 *   Header / hero card, Working Hours, Address + Map
 *   Restaurant Performance (current-period stat cards + summary)
 *   Reviews & Rating (derived from the real `comments` array)
 *   Recent Transactions + Top Customers
 *
 * Sections built as UI-complete placeholders, pending a backend endpoint
 * (marked with a `TODO(api)` comment wherever a real call needs dropping in):
 *   Linked Settlement Account
 *   Recent Voucher Redemptions
 *   Activity Timeline
 * ---------------------------------------------------------------------- */

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatPeriodDate = (value, includeYear = false) => {
  if (!value) return "";
  const rawValue = value.toString();
  const date = new Date(rawValue.includes("T") ? rawValue : `${rawValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
  });
};

const parseTimeToMinutes = (t) => {
  const [h, m] = (t || "").split(":").map(Number);
  if (Number.isNaN(h)) return null;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
};

const minutesTo12h = (mins) => {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

/**
 * A single day can carry more than one `listing_time` row (e.g. a lunch
 * shift and a dinner shift, or — as seen in the raw data — a duplicate/
 * overlapping range for the same shift). Rather than joining every raw
 * range into one long comma-separated string, merge overlapping or
 * contained ranges down to their real, non-redundant time blocks.
 */
const mergeHourRanges = (rawRanges) => {
  const parsed = rawRanges
    .map((range) => {
      const [start, end] = (range || "").split("-").map((s) => s.trim());
      return { start: parseTimeToMinutes(start), end: parseTimeToMinutes(end) };
    })
    .filter((r) => r.start !== null && r.end !== null)
    .sort((a, b) => a.start - b.start);

  const merged = [];
  parsed.forEach((range) => {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end);
    } else {
      merged.push({ ...range });
    }
  });

  return merged.map((r) => `${minutesTo12h(r.start)} – ${minutesTo12h(r.end)}`);
};

const isTruthyFlag = (value) => value === true || value === 1 || value === "1" || value === "true";

const isClosedHours = (hours) => {
  const normalized = hours?.toString().trim().toLowerCase().replace(/\s/g, "");
  return !normalized || normalized === "null-null";
};

const isOpen24Hours = (entry) => {
  if (isTruthyFlag(entry?.open_24_hours) || isTruthyFlag(entry?.is_open_24_hours)) return true;
  const normalized = entry?.hours?.toString().trim().replace(/\s/g, "");
  return normalized === "00:00-23:59";
};

const isClosedTimeEntry = (entry) => isTruthyFlag(entry?.is_closed) || isClosedHours(entry?.hours);

const isApiSuccess = (response) => {
  if (!response) return false;
  if (typeof response.data?.success !== "undefined") return response.data.success;
  if (typeof response.data?.status !== "undefined") return response.data.status;
  return response.status >= 200 && response.status < 300;
};

const extractPayload = (response) => {
  if (!response) return null;
  if (typeof response.data?.data !== "undefined") return response.data.data;
  return response.data || null;
};

const emptyValue = "—";

const PERFORMANCE_PERIODS = {
  all_time: { label: "All Time", period: "all_time" },
  weekly: { label: "Weekly", period: "weekly", groupBy: "day" },
  monthly: { label: "Monthly", period: "monthly", groupBy: "week" },
  yearly: { label: "Yearly", period: "yearly", groupBy: "month" },
};

const MONEY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const COUNT_FORMATTER = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const SETTLEMENT_STATUS_LABELS = {
  no_platform_fee: "No Platform Fee",
  awaiting_settlement: "Awaiting Settlement",
  settlement_completed: "Settlement Completed",
  cancelled: "Cancelled",
  pending: "Pending",
  paid: "Paid",
};

const hasMoneyValue = (value) => {
  if (value && typeof value === "object") return value.amount !== undefined && value.amount !== null;
  return value !== undefined && value !== null && value !== "";
};

const getMoneyAmount = (value) => {
  if (value && typeof value === "object" && value.amount !== undefined && value.amount !== null) {
    return Number(value.amount) || 0;
  }
  if (value === 0) return 0;
  return Number(value) || 0;
};

const formatMoney = (value) => (hasMoneyValue(value) ? MONEY_FORMATTER.format(getMoneyAmount(value)) : emptyValue);

const getCountValue = (value) => {
  if (value && typeof value === "object") {
    return Number(value.count ?? value.value ?? value.amount ?? 0) || 0;
  }
  return Number(value ?? 0) || 0;
};

const formatCount = (value) => COUNT_FORMATTER.format(getCountValue(value));

const formatTransactionDate = (value) => {
  if (!value) return emptyValue;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return emptyValue;
  return formatDateTime(value);
};

const formatComparison = (value) => {
  const comparison = Number(value ?? 0);
  if (!Number.isFinite(comparison)) return "0%";
  if (comparison > 0) return `↑ +${comparison.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
  if (comparison < 0) return `↓ ${comparison.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
  return "0%";
};

const formatSettlementStatus = (status) => {
  const raw = status?.toString().trim();
  if (!raw) return emptyValue;
  return SETTLEMENT_STATUS_LABELS[raw] || raw.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatPeriodLabel = (period) => {
  if (!period) return emptyValue;
  if (typeof period === "string") {
    if (period === "all_time") return "All Time";
    if (period === "custom") return "Custom Date Range";
    return period;
  }
  if (period.start_date && period.end_date) {
    const startValue = period.start_date.toString();
    const endValue = period.end_date.toString();
    const startYear = new Date(startValue.includes("T") ? startValue : `${startValue}T00:00:00`).getFullYear();
    const endYear = new Date(endValue.includes("T") ? endValue : `${endValue}T00:00:00`).getFullYear();
    return `${formatPeriodDate(period.start_date, startYear !== endYear)} – ${formatPeriodDate(period.end_date, true)}`;
  }
  return period.label || period.subtitle || emptyValue;
};

const formatInvoiceDisplay = (invoice) => {
  if (!invoice) return emptyValue;
  const invoiceNumber = invoice.invoice_number || invoice.number || invoice.id || invoice.invoice_id;
  const invoiceStatus = formatSettlementStatus(invoice.invoice_status || invoice.status);
  if (invoiceNumber && invoiceStatus !== emptyValue) return `${invoiceNumber} (${invoiceStatus})`;
  return invoiceNumber || invoiceStatus;
};

const getInvoiceId = (invoice) => invoice?.id || invoice?.invoice_id || null;

const renderInvoiceDisplay = (invoice, onViewInvoice) => {
  if (!invoice) return emptyValue;

  const invoiceId = getInvoiceId(invoice);
  const invoiceText = formatInvoiceDisplay(invoice);

  return (
    <span className="ar-invoice-cell">
      <span>{invoiceText}</span>
      {invoiceId && (
        <button type="button" className="ar-inline-link" onClick={() => onViewInvoice(invoiceId)}>
          View Invoice
        </button>
      )}
    </span>
  );
};

const getDashboardQuery = (listingId, periodKey, dateRange = {}) => {
  const config = periodKey === "custom"
    ? { period: "custom" }
    : PERFORMANCE_PERIODS[periodKey] || PERFORMANCE_PERIODS.all_time;
  const params = new URLSearchParams();
  params.append("vendor_listing_id", listingId);
  params.append("period", config.period);
  if (config.groupBy) {
    params.append("group_by", config.groupBy);
  }
  if (config.period === "custom") {
    params.append("date_from", dateRange.date_from);
    params.append("date_to", dateRange.date_to);
  }
  return params.toString();
};

const getPerformancePeriodLabel = (performanceData, fallback) => {
  const candidates = [
    performanceData?.date_range,
    performanceData?.selected_period,
    performanceData?.period_label,
    performanceData?.summary?.period,
    performanceData?.period,
  ];

  const label = candidates
    .map(formatPeriodLabel)
    .find((value) => (
      value
      && value !== emptyValue
      && !(performanceData?.period === "custom" && value === "Custom Date Range")
    ));

  return label || fallback;
};

const extractPaginator = (response) => {
  const payload = extractPayload(response);
  if (Array.isArray(payload)) return { data: payload, current_page: 1, last_page: 1 };
  if (Array.isArray(payload?.data)) return payload;
  if (Array.isArray(response?.data?.data?.data)) return response.data.data;
  return { data: [], current_page: 1, last_page: 1 };
};

const getMemberName = (transaction) => {
  const details = transaction?.member_details;
  const detailName = details?.full_name?.toString().trim();
  if (detailName) return detailName;

  const combinedDetailName = [details?.first_name, details?.last_name].filter(Boolean).join(" ").trim();
  if (combinedDetailName) return combinedDetailName;

  if (typeof transaction?.member?.full_name === "string" && transaction.member.full_name.trim()) {
    return transaction.member.full_name.trim();
  }

  if (typeof transaction?.member === "string" && transaction.member.trim()) {
    return transaction.member.trim();
  }

  return transaction?.member_email?.toString().trim() || emptyValue;
};

const getTransactionEmail = (transaction) => {
  const email = transaction?.member_email
    || transaction?.member_details?.email
    || transaction?.member?.email;
  return email?.toString().trim() || emptyValue;
};

const getTransactionAmount = (transaction) => {
  if (transaction?.amount_decimal !== undefined && transaction.amount_decimal !== null && transaction.amount_decimal !== "") {
    return Number(transaction.amount_decimal) || 0;
  }

  return Number(transaction?.amount_cents ?? 0) / 100;
};

const getTransactionTimestamp = (transaction) => (
  transaction?.transacted_at
  || transaction?.transaction_date
  || transaction?.posted_at
  || transaction?.created_at
);

const getTransactionMerchant = (transaction) => {
  const candidates = [
    transaction?.merchant,
    transaction?.restaurant,
    transaction?.category,
    transaction?.type,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    if (candidate && typeof candidate === "object") {
      const label = candidate.title || candidate.name || candidate.label || candidate.description;
      if (typeof label === "string" && label.trim()) return label.trim();
    }
  }

  return emptyValue;
};

const formatPrimaryOwnerName = (primaryOwner) => {
  if (!primaryOwner) return emptyValue;

  const fullName = primaryOwner.full_name?.trim();
  if (fullName) return fullName;

  const combinedName = [primaryOwner.first_name, primaryOwner.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return combinedName || emptyValue;
};

const formatPrimaryOwnerValue = (value) => value?.toString().trim() || emptyValue;

const formatBooleanLabel = (value) => {
  if (value === true || value === 1 || value === "1") return "Yes";
  if (value === false || value === 0 || value === "0") return "No";
  return null;
};

const getLinkedAccountPaginatorRows = (response) => {
  const payload = extractPayload(response);
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(response?.data?.data?.data)) return response.data.data.data;
  return [];
};

const getFirstArrayItem = (value) => (Array.isArray(value) && value.length ? value[0] : null);

const getBankAccountRecord = (account) => {
  const meta = account?.meta || {};
  const externalAccounts = account?.external_accounts || meta.external_accounts || {};

  return account?.bank_account
    || account?.external_account
    || getFirstArrayItem(account?.external_accounts?.data)
    || getFirstArrayItem(account?.external_accounts)
    || getFirstArrayItem(externalAccounts?.data)
    || getFirstArrayItem(externalAccounts)
    || null;
};

const getLinkedSettlementAccountDetails = (account) => {
  if (!account) return [];

  const bankAccount = getBankAccountRecord(account);
  const meta = account.meta || {};
  const businessProfile = meta.business_profile || {};
  const individual = meta.individual || {};
  const company = meta.company || {};
  const accountHolder = account.account_holder_name
    || bankAccount?.account_holder_name
    || businessProfile.name
    || company.name
    || [individual.first_name, individual.last_name].filter(Boolean).join(" ").trim();

  return [
    { label: "Stripe account ID", value: account.stripe_account_id },
    { label: "Onboarding status", value: formatSettlementStatus(account.onboarding_status) },
    { label: "Payouts enabled", value: formatBooleanLabel(account.payouts_enabled) },
    { label: "Charges enabled", value: formatBooleanLabel(account.charges_enabled) },
    { label: "Linked date", value: account.created_at ? formatDateTime(account.created_at) : null },
    { label: "Bank name", value: account.bank_name || bankAccount?.bank_name },
    { label: "Account holder", value: accountHolder },
    { label: "Account type", value: formatSettlementStatus(account.account_type || bankAccount?.account_type || bankAccount?.object) },
    { label: "Last four digits", value: account.last4 || account.last_four || bankAccount?.last4 },
  ].filter((item) => item.value !== undefined && item.value !== null && item.value !== "" && item.value !== emptyValue);
};

const getAccountStatusValue = (restaurant) => {
  const rawStatus = restaurant?.account_status?.toString().trim().toLowerCase();
  if (rawStatus === "active" || rawStatus === "inactive") return rawStatus;

  if (restaurant?.account_is_active === true) return "active";
  if (restaurant?.account_is_active === false) return "inactive";

  return null;
};

const getAccountStatusLabel = (accountStatus) => {
  if (accountStatus === "active") return "Active";
  if (accountStatus === "inactive") return "Inactive";
  return "Unknown";
};

const extractAccountStatusData = (response) => {
  const payload = extractPayload(response);
  if (!payload) return {};

  if (payload.account_status || typeof payload.account_is_active !== "undefined") return payload;
  if (payload.restaurant?.account_status || typeof payload.restaurant?.account_is_active !== "undefined") {
    return payload.restaurant;
  }
  if (payload.vendor_listing?.account_status || typeof payload.vendor_listing?.account_is_active !== "undefined") {
    return payload.vendor_listing;
  }

  return {};
};

const extractAccountStatusErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (responseData?.message) return responseData.message;
  if (typeof responseData?.error === "string" && responseData.error.trim()) return responseData.error;

  return extractErrorMessage(error) || "Unable to update the restaurant account status.";
};

const resolveAssetUrl = (value) => {
  const path = value?.toString().trim();
  if (!path) return "";
  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:") || path.startsWith("blob:")) return path;

  const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  if (!baseUrl) return path;

  return `${baseUrl}/${path.replace(/^\//, "")}`;
};

/* ============ small shared bits ============ */

const SectionCard = ({ title, subtitle, action, icon, className, children }) => (
  <div className={`rp-card ar-section-card ${className || ""}`}>
    <div className="rp-card__header">
      <div>
        <h2 className="rp-card__title ar-section-title">
          {icon && <span className="ar-section-icon">{icon}</span>}
          {title}
        </h2>
        {subtitle && <p className="rp-card__subtitle">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const StatCard = ({ label, value, accent, comparison, icon }) => (
  <div className="rp-stat-card ar-performance-stat-card">
    <div className="rp-stat-card__top">
      {icon && <span className="ar-performance-stat-card__icon" style={accent ? { color: accent } : undefined}>{icon}</span>}
      <span className="rp-stat-card__label">{label}</span>
    </div>
    <div className="rp-stat-card__body">
      <div className="rp-stat-card__value-wrap">
        <span className="rp-stat-card__value" style={accent ? { color: accent } : undefined}>
          {value}
        </span>
        {comparison && (
          <div className="rp-stat-card__delta-wrap">
            <span className={`rp-stat-card__delta rp-stat-card__delta--${comparison.tone}`}>
              {comparison.value}
            </span>
            <span className="rp-stat-card__delta-subtitle">vs previous period</span>
          </div>
        )}
      </div>
    </div>
  </div>
);

const SettlementMetricCard = ({ label, value, icon, children }) => (
  <div className="ar-settlement-metric-card">
    <div className="ar-settlement-metric-card__top">
      {icon && <span className="ar-settlement-metric-card__icon">{icon}</span>}
      <span className="ar-detail-item__label">{label}</span>
    </div>
    <div className="ar-settlement-metric-card__value">{value}</div>
    {children && <div className="ar-settlement-metric-card__support">{children}</div>}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    active: "ar-badge--green",
    approved: "ar-badge--green",
    pending: "ar-badge--orange",
    rejected: "ar-badge--red",
    inactive: "ar-badge--grey",
    "awaiting settlement": "ar-badge--orange",
    "settlement completed": "ar-badge--green",
    "no platform fee": "ar-badge--grey",
    paid: "ar-badge--green",
    cancelled: "ar-badge--red",
  };
  const key = (status || "").toLowerCase();
  return <span className={`ar-badge ${map[key] || "ar-badge--grey"}`}>{status || "N/A"}</span>;
};

/**
 * Wraps a DataTable so it always scrolls horizontally within its own card
 * instead of forcing the card (and the page) wider. Applied as an inline
 * style as well as a class so it's guaranteed to work even before/without
 * the accompanying stylesheet.
 */
const TableScroll = ({ children }) => (
  <div className="ar-table-scroll" style={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
    {children}
  </div>
);

const PERFORMANCE_CARD_DEFS = [
  { key: "total_member_spend", label: "Total Member Spend", type: "money", accent: "#1ba97f", icon: <SpendIcon /> },
  { key: "total_transactions", label: "Total Transactions", type: "count", icon: <ActivityDotIcon /> },
  { key: "unique_members", label: "Unique Members", type: "count", icon: <ActivityDotIcon /> },
  { key: "voucher_redemption_amount", label: "Voucher Redemption Amount", type: "money", icon: <GiftIcon /> },
  { key: "restaurant_net_revenue", label: "Restaurant Net Revenue", type: "money", accent: "#0ea5e9", icon: <DollarIcon /> },
  { key: "average_ticket_size", label: "Average Ticket Size", type: "money", icon: <PercentIcon /> },
];

const PERIOD_SUMMARY_DEFS = [
  { key: "total_member_spend", label: "Total Member Spend" },
  { key: "voucher_redemption_amount", label: "Voucher Redemption Amount" },
  { key: "platform_fee", label: "Platform Fee" },
  { key: "restaurant_net_revenue", label: "Restaurant Net Revenue" },
];

const MiniPerformanceGraph = ({ buckets }) => {
  const labels = buckets.map((bucket) => (
    bucket.label || formatPeriodLabel({ start_date: bucket.start_date, end_date: bucket.end_date })
  ));
  const series = [
    {
      name: "Total Member Spend",
      data: buckets.map((bucket) => getMoneyAmount(bucket.total_member_spend)),
    },
    {
      name: "Voucher Redemption Amount",
      data: buckets.map((bucket) => getMoneyAmount(bucket.voucher_redemption_amount)),
    },
    {
      name: "Restaurant Net Revenue",
      data: buckets.map((bucket) => getMoneyAmount(bucket.restaurant_net_revenue)),
    },
  ];
  const options = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "inherit",
    },
    colors: ["#1ba97f", "#e23d3d", "#0ea5e9"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    markers: {
      size: 4,
      strokeWidth: 2,
      hover: { size: 6 },
    },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 3,
      padding: { left: 8, right: 16, top: 8, bottom: 0 },
    },
    xaxis: {
      categories: labels,
      labels: {
        rotate: -30,
        trim: false,
        style: { fontSize: "11px", colors: "#6b7280" },
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter: (value) => MONEY_FORMATTER.format(Number(value) || 0),
        style: { fontSize: "11px", colors: "#6b7280" },
      },
    },
    legend: {
      position: "bottom",
      horizontalAlign: "left",
      markers: { radius: 12 },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (value) => MONEY_FORMATTER.format(Number(value) || 0) },
    },
  };

  return (
    <div className="ar-performance-chart">
      {buckets.length ? (
        <ReactApexChart options={options} series={series} type="line" height={320} />
      ) : (
        <p className="ar-pending__text ar-compact-empty">No graph data found.</p>
      )}
    </div>
  );
};

const AdminRestaurantDetailPage = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialRestaurantData = location.state?.restaurantData || null;

  const [restaurantData, setRestaurantData] = useState(initialRestaurantData);
  const [loadingRestaurant, setLoadingRestaurant] = useState(!initialRestaurantData);

  const [performanceData, setPerformanceData] = useState(null);
  const [performancePeriod, setPerformancePeriod] = useState("all_time");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [appliedCustomDateRange, setAppliedCustomDateRange] = useState({});
  const [customDateError, setCustomDateError] = useState("");
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [performanceError, setPerformanceError] = useState(null);
  const [linkedSettlementAccount, setLinkedSettlementAccount] = useState(null);
  const [loadingLinkedSettlementAccount, setLoadingLinkedSettlementAccount] = useState(false);
  const [linkedSettlementAccountError, setLinkedSettlementAccountError] = useState(null);
  const [unlinkingSettlementAccount, setUnlinkingSettlementAccount] = useState(false);
  const [showUnlinkSettlementModal, setShowUnlinkSettlementModal] = useState(false);

  const [reviewsData, setReviewsData] = useState(null);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsHasMore, setReviewsHasMore] = useState(true);
  const [loadingReviewsMore, setLoadingReviewsMore] = useState(false);

  const [vouchersData, setVouchersData] = useState(null);
  const [loadingVouchers, setLoadingVouchers] = useState(false);
  const [vouchersPage, setVouchersPage] = useState(1);
  const [vouchersHasMore, setVouchersHasMore] = useState(true);
  const [loadingVouchersMore, setLoadingVouchersMore] = useState(false);

  const [customersData, setCustomersData] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customersPage, setCustomersPage] = useState(1);
  const [customersHasMore, setCustomersHasMore] = useState(true);
  const [loadingCustomersMore, setLoadingCustomersMore] = useState(false);

  const [transactionsData, setTransactionsData] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsHasMore, setTransactionsHasMore] = useState(true);
  const [loadingTransactionsMore, setLoadingTransactionsMore] = useState(false);
  const [transactionsError, setTransactionsError] = useState(null);

  const [settlementInvoicesData, setSettlementInvoicesData] = useState(null);
  const [loadingSettlementInvoices, setLoadingSettlementInvoices] = useState(false);
  const [settlementInvoicesPage, setSettlementInvoicesPage] = useState(1);
  const [settlementInvoicesHasMore, setSettlementInvoicesHasMore] = useState(true);
  const [loadingSettlementInvoicesMore, setLoadingSettlementInvoicesMore] = useState(false);
  const [settlementInvoicesError, setSettlementInvoicesError] = useState(null);

  const [pointsLedgerData, setPointsLedgerData] = useState(null);
  const [loadingPointsLedger, setLoadingPointsLedger] = useState(false);
  const [pointsLedgerPage, setPointsLedgerPage] = useState(1);
  const [pointsLedgerHasMore, setPointsLedgerHasMore] = useState(true);
  const [loadingPointsLedgerMore, setLoadingPointsLedgerMore] = useState(false);
  const [showAccountStatusModal, setShowAccountStatusModal] = useState(false);
  const [pendingAccountStatus, setPendingAccountStatus] = useState(null);
  const [updatingAccountStatus, setUpdatingAccountStatus] = useState(false);

  useEffect(() => {
    toastr.options = {
      positionClass: "toast-bottom-left",
      timeOut: 5000,
      closeButton: true,
      progressBar: true,
    };
  }, []);

  const fetchRestaurantDetails = useCallback(async () => {
    try {
      setLoadingRestaurant(true);
      const endpoint = endpoints.adminSingleRestaurant.replace("{{listing_id}}", restaurantId);
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const payload = extractPayload(response);
        if (payload) setRestaurantData(payload);
      }
    } catch (error) {
      console.error("Failed to fetch restaurant details:", error);
      toastr.error("Failed to load restaurant details");
    } finally {
      setLoadingRestaurant(false);
    }
  }, [restaurantId]);

  const fetchPerformanceData = useCallback(async (period = "all_time", dateRange = {}) => {
    try {
      setLoadingPerformance(true);
      setPerformanceError(null);
      const endpoint = `${endpoints.adminSettlementsDashboard}?${getDashboardQuery(restaurantId, period, dateRange)}`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const payload = extractPayload(response);
        if (payload) setPerformanceData(payload);
      }
    } catch (error) {
      console.error("Failed to fetch performance data:", error);
      setPerformanceError("Unable to load restaurant performance and settlement data.");
    } finally {
      setLoadingPerformance(false);
    }
  }, [restaurantId]);

  const fetchLinkedSettlementAccount = useCallback(async () => {
    if (!restaurantId) return;

    try {
      setLoadingLinkedSettlementAccount(true);
      setLinkedSettlementAccountError(null);
      const params = new URLSearchParams();
      params.append("vendor_listing_id", restaurantId);
      params.append("per_page", 1);

      const response = await get(`${endpoints.adminRestaurantsConnectAccounts}?${params.toString()}`);
      if (isApiSuccess(response)) {
        const rows = getLinkedAccountPaginatorRows(response);
        setLinkedSettlementAccount(rows[0] || null);
      }
    } catch (error) {
      console.error("Failed to fetch linked settlement account:", error);
      setLinkedSettlementAccountError("Unable to load linked settlement account.");
      setLinkedSettlementAccount(null);
    } finally {
      setLoadingLinkedSettlementAccount(false);
    }
  }, [restaurantId]);

  const fetchReviewsData = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingReviewsMore(true);
    } else {
      setLoadingReviews(true);
    }

    try {
      const endpoint = `${endpoints.adminRestaurantReviews.replace("{{listing_id}}", restaurantId)}&page=${pageNum}`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const payload = extractPayload(response);
        if (payload) {
          if (isLoadMore) {
            setReviewsData((prev) => ({
              ...payload,
              data: [...(prev?.data || []), ...(payload.data || [])],
            }));
          } else {
            setReviewsData(payload);
          }

          const currentPage = payload.current_page || 1;
          const lastPage = payload.last_page || 1;
          setReviewsHasMore(currentPage < lastPage);
          setReviewsPage(currentPage);
        }
      }
    } catch (error) {
      console.error("Failed to fetch reviews data:", error);
    } finally {
      setLoadingReviews(false);
      setLoadingReviewsMore(false);
    }
  }, [restaurantId]);

  const fetchVouchersData = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingVouchersMore(true);
    } else {
      setLoadingVouchers(true);
    }

    try {
      const endpoint = `${endpoints.adminRestaurantVouchers.replace("{{listing_id}}", restaurantId)}&page=${pageNum}`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const payload = extractPayload(response);
        if (payload) {
          if (isLoadMore) {
            setVouchersData((prev) => ({
              ...payload,
              data: [...(prev?.data || []), ...(payload.data || [])],
            }));
          } else {
            setVouchersData(payload);
          }

          const currentPage = payload.current_page || 1;
          const lastPage = payload.last_page || 1;
          setVouchersHasMore(currentPage < lastPage);
          setVouchersPage(currentPage);
        }
      }
    } catch (error) {
      console.error("Failed to fetch vouchers data:", error);
    } finally {
      setLoadingVouchers(false);
      setLoadingVouchersMore(false);
    }
  }, [restaurantId]);

  const fetchCustomersData = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingCustomersMore(true);
    } else {
      setLoadingCustomers(true);
    }

    try {
      const endpoint = `${endpoints.adminRestaurantCustomers.replace("{{listing_id}}", restaurantId)}&page=${pageNum}`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const payload = extractPayload(response);
        if (payload) {
          if (isLoadMore) {
            setCustomersData((prev) => ({
              ...payload,
              data: [...(prev?.data || []), ...(payload.data || [])],
            }));
          } else {
            setCustomersData(payload);
          }

          const currentPage = payload.current_page || 1;
          const lastPage = payload.last_page || 1;
          setCustomersHasMore(currentPage < lastPage);
          setCustomersPage(currentPage);
        }
      }
    } catch (error) {
      console.error("Failed to fetch customers data:", error);
    } finally {
      setLoadingCustomers(false);
      setLoadingCustomersMore(false);
    }
  }, [restaurantId]);

  const fetchTransactionsData = useCallback(async (pageNum = 1, isLoadMore = false, period = "all_time", dateRange = {}) => {
    if (!restaurantId) return;

    if (isLoadMore) {
      setLoadingTransactionsMore(true);
    } else {
      setLoadingTransactions(true);
    }

    try {
      setTransactionsError(null);
      const params = new URLSearchParams();
      params.append("vendor_listing_id", restaurantId);
      params.append("period", period);
      if (period === "custom") {
        params.append("date_from", dateRange.date_from);
        params.append("date_to", dateRange.date_to);
      }
      params.append("page", pageNum);
      params.append("per_page", 10);

      const response = await get(`${endpoints.adminRecruitmentLeakageFullTransactionFeed}?${params.toString()}`);
      if (isApiSuccess(response)) {
        const paginator = extractPaginator(response);

        if (isLoadMore) {
          setTransactionsData((prev) => ({
            ...paginator,
            data: [...(prev?.data || []), ...(paginator.data || [])],
          }));
        } else {
          setTransactionsData(paginator);
        }

        const currentPage = paginator.current_page || 1;
        const lastPage = paginator.last_page || 1;
        setTransactionsHasMore(currentPage < lastPage);
        setTransactionsPage(currentPage);
      }
    } catch (error) {
      console.error("Failed to fetch recent transactions:", error);
      if (!isLoadMore) setTransactionsData({ data: [], current_page: 1, last_page: 1 });
      setTransactionsError("Unable to load recent transactions.");
    } finally {
      setLoadingTransactions(false);
      setLoadingTransactionsMore(false);
    }
  }, [restaurantId]);

  const fetchSettlementInvoicesData = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingSettlementInvoicesMore(true);
    } else {
      setLoadingSettlementInvoices(true);
    }

    try {
      setSettlementInvoicesError(null);
      const params = new URLSearchParams();
      params.append("vendor_listing_id", restaurantId);
      params.append("page", pageNum);
      params.append("per_page", 20);
      const endpoint = `${endpoints.adminSettlementInvoices}?${params.toString()}`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const payload = extractPayload(response);
        if (payload) {
          if (isLoadMore) {
            setSettlementInvoicesData((prev) => ({
              ...payload,
              data: [...(prev?.data || []), ...(payload.data || [])],
            }));
          } else {
            setSettlementInvoicesData(payload);
          }

          const currentPage = payload.current_page || 1;
          const lastPage = payload.last_page || 1;
          setSettlementInvoicesHasMore(currentPage < lastPage);
          setSettlementInvoicesPage(currentPage);
        }
      }
    } catch (error) {
      console.error("Failed to fetch settlement invoices data:", error);
      setSettlementInvoicesError("Unable to load weekly settlement history.");
    } finally {
      setLoadingSettlementInvoices(false);
      setLoadingSettlementInvoicesMore(false);
    }
  }, [restaurantId]);

  const fetchPointsLedgerData = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingPointsLedgerMore(true);
    } else {
      setLoadingPointsLedger(true);
    }

    try {
      const endpoint = `${endpoints.adminRestaurantPointsLedger.replace("{{listing_id}}", restaurantId)}&page=${pageNum}`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const payload = extractPayload(response);
        if (payload) {
          if (isLoadMore) {
            setPointsLedgerData((prev) => ({
              ...payload,
              data: [...(prev?.data || []), ...(payload.data || [])],
            }));
          } else {
            setPointsLedgerData(payload);
          }

          const currentPage = payload.current_page || 1;
          const lastPage = payload.last_page || 1;
          setPointsLedgerHasMore(currentPage < lastPage);
          setPointsLedgerPage(currentPage);
        }
      }
    } catch (error) {
      console.error("Failed to fetch points ledger data:", error);
    } finally {
      setLoadingPointsLedger(false);
      setLoadingPointsLedgerMore(false);
    }
  }, [restaurantId]);

  const openAccountStatusModal = (nextStatus) => {
    if (!nextStatus || updatingAccountStatus) return;
    setPendingAccountStatus(nextStatus);
    setShowAccountStatusModal(true);
  };

  const closeAccountStatusModal = () => {
    if (updatingAccountStatus) return;
    setShowAccountStatusModal(false);
    setPendingAccountStatus(null);
  };

  const confirmAccountStatusUpdate = async () => {
    if (!pendingAccountStatus || updatingAccountStatus) return;

    try {
      setUpdatingAccountStatus(true);
      const response = await patchRequest(
        `/api/admin/restaurants/${restaurantId}/account-status`,
        { status: pendingAccountStatus }
      );

      if (isApiSuccess(response)) {
        const accountStatusData = extractAccountStatusData(response);
        const nextAccountStatus = accountStatusData.account_status || pendingAccountStatus;
        const nextAccountIsActive = typeof accountStatusData.account_is_active !== "undefined"
          ? accountStatusData.account_is_active
          : nextAccountStatus === "active";

        setRestaurantData((prev) => ({
          ...(prev || {}),
          account_status: nextAccountStatus,
          account_is_active: nextAccountIsActive,
        }));
        setShowAccountStatusModal(false);
        setPendingAccountStatus(null);
        toastr.success(response.data?.message || "Restaurant account status updated successfully.");
      } else {
        toastr.error(response.data?.message || "Unable to update the restaurant account status.");
      }
    } catch (error) {
      console.error("Failed to update restaurant account status:", error);
      toastr.error(extractAccountStatusErrorMessage(error));
    } finally {
      setUpdatingAccountStatus(false);
    }
  };

  useEffect(() => {
    fetchRestaurantDetails();
  }, [fetchRestaurantDetails]);

  useEffect(() => {
    fetchPerformanceData(performancePeriod, appliedCustomDateRange);
  }, [fetchPerformanceData, performancePeriod, appliedCustomDateRange]);

  useEffect(() => {
    fetchLinkedSettlementAccount();
  }, [fetchLinkedSettlementAccount]);

  useEffect(() => {
    fetchReviewsData();
  }, [fetchReviewsData]);

  useEffect(() => {
    fetchVouchersData();
  }, [fetchVouchersData]);

  useEffect(() => {
    fetchCustomersData();
  }, [fetchCustomersData]);

  useEffect(() => {
    setTransactionsPage(1);
    setTransactionsHasMore(true);
    setTransactionsData(null);
    fetchTransactionsData(1, false, performancePeriod, appliedCustomDateRange);
  }, [fetchTransactionsData, performancePeriod, appliedCustomDateRange]);

  useEffect(() => {
    fetchSettlementInvoicesData();
  }, [fetchSettlementInvoicesData]);

  useEffect(() => {
    fetchPointsLedgerData();
  }, [fetchPointsLedgerData]);

  const handleLoadMoreReviews = () => {
    if (!loadingReviewsMore && reviewsHasMore) {
      fetchReviewsData(reviewsPage + 1, true);
    }
  };

  const handleLoadMoreVouchers = () => {
    if (!loadingVouchersMore && vouchersHasMore) {
      fetchVouchersData(vouchersPage + 1, true);
    }
  };

  const handleLoadMoreCustomers = () => {
    if (!loadingCustomersMore && customersHasMore) {
      fetchCustomersData(customersPage + 1, true);
    }
  };

  const handleLoadMoreTransactions = () => {
    if (!loadingTransactionsMore && transactionsHasMore) {
      fetchTransactionsData(transactionsPage + 1, true, performancePeriod, appliedCustomDateRange);
    }
  };

  const handleLoadMoreSettlementInvoices = () => {
    if (!loadingSettlementInvoicesMore && settlementInvoicesHasMore) {
      fetchSettlementInvoicesData(settlementInvoicesPage + 1, true);
    }
  };

  const handleLoadMorePointsLedger = () => {
    if (!loadingPointsLedgerMore && pointsLedgerHasMore) {
      fetchPointsLedgerData(pointsLedgerPage + 1, true);
    }
  };

  const handlePerformancePeriodSelect = (period) => {
    setPerformancePeriod(period);
    setCustomDateFrom("");
    setCustomDateTo("");
    setAppliedCustomDateRange({});
    setCustomDateError("");
    setShowCustomDatePicker(false);
  };

  const handleCustomPerformanceApply = () => {
    if (!customDateFrom || !customDateTo) {
      setCustomDateError("Both From date and To date are required.");
      return;
    }

    if (customDateFrom > customDateTo) {
      setCustomDateError("From date cannot be after To date.");
      return;
    }

    setCustomDateError("");
    setPerformancePeriod("custom");
    setAppliedCustomDateRange({
      date_from: customDateFrom,
      date_to: customDateTo,
    });
    setShowCustomDatePicker(false);
  };

  const handleCustomPerformanceReset = () => {
    handlePerformancePeriodSelect("all_time");
  };

  const handleUnlinkSettlementAccountClick = () => {
    if (!linkedSettlementAccount?.id || unlinkingSettlementAccount) return;
    setShowUnlinkSettlementModal(true);
  };

  const closeUnlinkSettlementModal = () => {
    if (unlinkingSettlementAccount) return;
    setShowUnlinkSettlementModal(false);
  };

  const confirmUnlinkSettlementAccount = async () => {
    if (!linkedSettlementAccount?.id || unlinkingSettlementAccount) return;

    try {
      setUnlinkingSettlementAccount(true);
      const deleteEndpoint = endpoints.adminDeleteRestaurantConnectAccount.replace(
        "{{connect_account_id}}",
        linkedSettlementAccount.id
      );
      const response = await deleteRequest(deleteEndpoint);

      if (isApiSuccess(response)) {
        toastr.success(response.data?.message || "Settlement account unlinked successfully.");
        setShowUnlinkSettlementModal(false);
        setLinkedSettlementAccount(null);
        fetchLinkedSettlementAccount();
      } else {
        toastr.error(response.data?.message || "Unable to unlink settlement account.");
      }
    } catch (error) {
      console.error("Failed to unlink settlement account:", error);
      toastr.error(extractErrorMessage(error) || "Unable to unlink settlement account.");
    } finally {
      setUnlinkingSettlementAccount(false);
    }
  };

  /* ---- derived: working hours, grouped by day ---- */

  const workingHours = useMemo(() => {
    const byDay = {};
    const listingTime = restaurantData?.listing_time || restaurantData?.listingTime;
    (Array.isArray(listingTime) ? listingTime : []).forEach((entry) => {
      const day = (entry?.day || "").toString().toLowerCase();
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(entry);
    });
    return DAY_ORDER.map((day) => {
      const entries = byDay[day] || [];
      const ranges = entries
        .filter((entry) => !isOpen24Hours(entry) && !isClosedTimeEntry(entry))
        .map((entry) => entry.hours);
      const mergedRanges = mergeHourRanges(ranges);

      return {
        day: day.charAt(0).toUpperCase() + day.slice(1),
        ranges: entries.some(isOpen24Hours)
          ? ["Open 24 Hours"]
          : mergedRanges.length
            ? mergedRanges
            : ["Closed"],
      };
    });
  }, [restaurantData]);

  /* ---- derived: recent transactions ---- */

  const transactionColumns = useMemo(() => [
    { key: "date", label: "Date", width: "1.3fr" },
    { key: "member", label: "Member", width: "1.2fr" },
    { key: "email", label: "Email", width: "1.4fr" },
    { key: "merchant", label: "Merchant", width: "1.2fr" },
    { key: "amountDisplay", label: "Amount", width: "1fr", align: "right" },
    { key: "pointsEarned", label: "Points Earned", width: "1fr", align: "right", render: (r) => <span className="nt-points">{r.pointsEarned}</span> },
    { key: "status", label: "Status", width: "1fr", align: "center", render: (r) => <StatusBadge status={r.status} /> },
  ], []);

  const transactionRows = useMemo(
    () =>
      (transactionsData?.data || []).map((t) => ({
        id: t.id,
        date: formatTransactionDate(getTransactionTimestamp(t)),
        member: getMemberName(t),
        email: getTransactionEmail(t),
        merchant: getTransactionMerchant(t),
        amountDisplay: MONEY_FORMATTER.format(getTransactionAmount(t)),
        pointsEarned: formatCount(t.points_earned ?? 0),
        status: formatSettlementStatus(t.status),
      })),
    [transactionsData]
  );

  /* ---- derived: vouchers ---- */

  const voucherColumns = useMemo(() => [
    { key: "code", label: "Code", width: "1.2fr" },
    { key: "customer", label: "Customer", width: "1.2fr" },
    { key: "reward", label: "Reward", width: "1fr" },
    { key: "points", label: "Points", width: "0.8fr", align: "right" },
    { key: "amount", label: "Amount", width: "0.8fr", align: "right" },
    { key: "status", label: "Status", width: "0.8fr", align: "center", render: (r) => <StatusBadge status={r.status === "active" ? "Active" : r.status} /> },
    { key: "redeemedAt", label: "Redeemed", width: "1fr" },
  ], []);

  const voucherRows = useMemo(
    () =>
      (vouchersData?.data || []).map((v) => ({
        id: v.id,
        code: v.code,
        customer: v.customer?.name || `User #${v.customer?.id}`,
        reward: v.reward?.name || "N/A",
        points: v.points?.toLocaleString() || "N/A",
        amount: `$${v.amount?.toFixed(2) || "0.00"}`,
        status: v.display_status || v.status,
        redeemedAt: v.redeemed_at ? formatDateOnly(v.redeemed_at) : "N/A",
      })),
    [vouchersData]
  );

  /* ---- derived: points ledger ---- */

  const pointsLedgerColumns = useMemo(() => [
    { key: "dateTime", label: "Date", width: "1.2fr" },
    { key: "member", label: "Member", width: "1.2fr" },
    { key: "type", label: "Type", width: "0.8fr" },
    { key: "reference", label: "Reference", width: "1fr" },
    { key: "transactionAmount", label: "Amount", width: "0.8fr", align: "right" },
    { key: "points", label: "Points", width: "0.8fr", align: "right", render: (r) => <span className={r.points >= 0 ? "nt-points" : "nt-points nt-points--negative"}>{r.points >= 0 ? "+" : ""}{r.points}</span> },
  ], []);

  const pointsLedgerRows = useMemo(
    () =>
      (pointsLedgerData?.data || []).map((entry) => ({
        id: entry.id,
        dateTime: entry.date_time,
        member: entry.member_name || entry.member?.name || `User #${entry.member?.id}`,
        type: entry.display_type || entry.type,
        reference: entry.transaction_reference || entry.reference || "N/A",
        transactionAmount: `$${entry.transaction_amount?.toFixed(2) || "0.00"}`,
        points: entry.points || 0,
      })),
    [pointsLedgerData]
  );

  /* ---- derived: settlement invoices ---- */

  const settlementInvoiceColumns = useMemo(() => [
    { key: "week", label: "Week", width: "1.4fr" },
    { key: "totalMemberSpend", label: "Total Member Spend", width: "1fr", align: "right" },
    { key: "pendingPlatformFee", label: "Pending Platform Fee", width: "1fr", align: "right" },
    { key: "paidPlatformFee", label: "Paid Platform Fee", width: "1fr", align: "right" },
    { key: "totalPlatformFee", label: "Total Platform Fee", width: "1fr", align: "right" },
    { key: "invoiceStatus", label: "Invoice Status", width: "0.9fr", align: "center", render: (r) => <StatusBadge status={r.invoiceStatus} /> },
    { key: "invoice", label: "Invoice", width: "1.2fr", align: "right" },
  ], []);

  const settlementInvoiceRows = useMemo(
    () =>
      (settlementInvoicesData?.data || []).map((invoice) => ({
        id: invoice.id,
        week: formatPeriodLabel(invoice.period || {
          start_date: invoice.period_start,
          end_date: invoice.period_end,
          label: invoice.period_label,
        }),
        totalMemberSpend: formatMoney(invoice.total_member_spend),
        pendingPlatformFee: formatMoney(invoice.pending_platform_fee),
        paidPlatformFee: formatMoney(invoice.paid_platform_fee),
        totalPlatformFee: formatMoney(invoice.total_platform_fee),
        invoiceStatus: formatSettlementStatus(invoice.invoice_status || invoice.status),
        invoice: renderInvoiceDisplay(invoice.invoice || invoice, (invoiceId) => navigate(`/admin/settlements/invoices/${invoiceId}`)),
      })),
    [navigate, settlementInvoicesData]
  );

  /* ---- derived: top customers (from restaurant customers API) ---- */

  const topCustomerColumns = useMemo(() => [
    { key: "customer", label: "Customer", width: "1.4fr" },
    { key: "visits", label: "Visits", width: "1fr", align: "right" },
    { key: "spendDisplay", label: "Total Spend", width: "1fr", align: "right" },
    { key: "pointsEarned", label: "Points Earned", width: "1fr", align: "right" },
  ], []);

  const topCustomerRows = useMemo(() => {
    return (customersData?.data || [])
      .map((c) => ({
        id: c.member.id,
        customer: c.member.name || c.member.email || `User #${c.member.id}`,
        visits: c.transaction_count,
        spend: c.total_matched_spend,
        spendDisplay: `$${c.total_matched_spend?.toFixed(2) || "0.00"}`,
        pointsEarned: c.points_earned?.toLocaleString() || "0",
      }))
      .sort((a, b) => b.spend - a.spend);
  }, [customersData]);

  /* ---- derived: reviews ---- */

  const reviewColumns = useMemo(() => [
    { key: "date", label: "Date", width: "1.2fr" },
    { key: "customer", label: "Customer", width: "1.2fr" },
    { key: "star", label: "Rating", width: "0.8fr", render: (r) => `${r.star} ★` },
    { key: "message", label: "Review", width: "2fr" },
  ], []);

  const reviewRows = useMemo(
    () =>
      (reviewsData?.data || []).map((c) => ({
        id: c.id,
        date: formatDateOnly(c.created_at),
        customer: c.user?.full_name || `User #${c.user_id}`,
        star: c.star || "N/A",
        message: c.message || "N/A",
      })),
    [reviewsData]
  );

  const ratingBreakdown = useMemo(() => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    (reviewsData?.data || []).forEach((c) => {
      const rounded = Math.round(Number(c.star || 0));
      if (counts[rounded] !== undefined) counts[rounded] += 1;
    });
    const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
    return [5, 4, 3, 2, 1].map((star) => ({
      star,
      count: counts[star],
      pct: Math.round((counts[star] / total) * 100),
    }));
  }, [reviewsData]);

  const performanceCards = useMemo(() => {
    const summaryCards = performanceData?.summary_cards || {};
    return PERFORMANCE_CARD_DEFS.map((definition) => {
      const card = summaryCards[definition.key] || {};
      const rawValue = definition.type === "count"
        ? formatCount(card.count ?? card.value ?? card.amount)
        : formatMoney(card);
      const comparisonValue = Number(card.comparison_percentage ?? 0);
      return {
        ...definition,
        value: rawValue,
        comparison: {
          value: formatComparison(comparisonValue),
          tone: comparisonValue < 0 ? "negative" : "positive",
        },
      };
    });
  }, [performanceData]);

  const graphBuckets = useMemo(
    () => (Array.isArray(performanceData?.graph) ? performanceData.graph : []),
    [performanceData]
  );

  const currentSettlement = performanceData?.current_settlement || {};
  const currentSettlementPeriod = formatPeriodLabel(currentSettlement.period || performanceData?.period);
  const currentSettlementStatus = currentSettlement.display_status || formatSettlementStatus(currentSettlement.status);
  const hasAppliedCustomRange = Boolean(appliedCustomDateRange.date_from && appliedCustomDateRange.date_to);
  const customDateRangeLabel = hasAppliedCustomRange
    ? `${formatPeriodDate(appliedCustomDateRange.date_from, true)} - ${formatPeriodDate(appliedCustomDateRange.date_to, true)}`
    : "Select date range";
  const performancePeriodFallback = performancePeriod === "custom" && appliedCustomDateRange.date_from && appliedCustomDateRange.date_to
    ? formatPeriodLabel({ start_date: appliedCustomDateRange.date_from, end_date: appliedCustomDateRange.date_to })
    : PERFORMANCE_PERIODS[performancePeriod]?.label || PERFORMANCE_PERIODS.all_time.label;
  const selectedPerformancePeriodLabel = getPerformancePeriodLabel(
    performanceData,
    performancePeriodFallback
  );

  const categories = restaurantData?.categories || [];
  const address = restaurantData?.address;
  const mapSrc = address?.latitude && address?.longitude
    ? `https://maps.google.com/maps?q=${address.latitude},${address.longitude}&z=15&output=embed`
    : null;
  const primaryOwner = restaurantData?.primary_owner || null;
  const linkedSettlementAccountDetails = getLinkedSettlementAccountDetails(linkedSettlementAccount);
  const restaurantImageUrl = resolveAssetUrl(restaurantData?.banner_url);
  const logoUrl = resolveAssetUrl(restaurantData?.logo);
  const accountStatus = getAccountStatusValue(restaurantData);
  const accountStatusLabel = getAccountStatusLabel(accountStatus);
  const nextAccountStatus = accountStatus === "active" ? "inactive" : accountStatus === "inactive" ? "active" : null;
  const accountActionLabel = accountStatus === "active" ? "Deactivate" : accountStatus === "inactive" ? "Activate" : null;
  const accountConfirmTitle = pendingAccountStatus === "inactive"
    ? "Deactivate Restaurant Account"
    : "Activate Restaurant Account";
  const accountConfirmMessage = pendingAccountStatus === "inactive"
    ? "Are you sure you want to deactivate this restaurant account? The restaurant will no longer be available for transaction matching, customer listings, new game progress, or Restaurant Portal access. Historical records will remain available."
    : "Are you sure you want to activate this restaurant account? Future transaction matching, customer visibility, new game eligibility, and Restaurant Portal access will be restored.";
  const accountConfirmText = pendingAccountStatus === "inactive" ? "Deactivate" : "Activate";

  return (
    <div className="page-content rp-page-content">
      <style>{`
        .ar-performance-header-action {
          align-items: flex-end;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .ar-performance-controls {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: flex-end;
        }
        .ar-performance-date-range {
          color: #6b7280;
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
        }
        .ar-date-range-picker {
          position: relative;
        }
        .ar-date-range-button {
          align-items: center;
          background: #fff;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          color: #374151;
          display: inline-flex;
          font-size: 13px;
          font-weight: 600;
          gap: 8px;
          min-height: 36px;
          min-width: 190px;
          padding: 7px 10px;
          white-space: nowrap;
        }
        .ar-date-range-button--active {
          border-color: #ff7a00;
          color: #111827;
        }
        .ar-date-range-button svg {
          flex: 0 0 auto;
          height: 16px;
          width: 16px;
        }
        .ar-date-range-popover {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
          padding: 12px;
          position: absolute;
          right: 0;
          top: calc(100% + 8px);
          width: 300px;
          z-index: 20;
        }
        .ar-date-range-fields {
          display: grid;
          gap: 8px;
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .ar-date-range-fields label {
          color: #6b7280;
          display: flex;
          flex-direction: column;
          font-size: 12px;
          font-weight: 600;
          gap: 4px;
        }
        .ar-date-range-fields input {
          border: 1px solid #d1d5db;
          border-radius: 6px;
          color: #111827;
          font-size: 13px;
          min-height: 34px;
          padding: 6px 8px;
        }
        .ar-performance-range-error {
          color: #dc2626;
          display: block;
          font-size: 12px;
          font-weight: 600;
          margin-top: 8px;
          text-align: left;
        }
        .ar-date-range-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          margin-top: 10px;
        }
        .ar-performance-card-grid {
          display: grid;
          gap: 12px;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          margin-bottom: 20px;
        }
        .ar-performance-stat-card {
          gap: 12px;
          min-width: 0;
          padding: 14px;
        }
        .ar-performance-stat-card .rp-stat-card__top {
          align-items: center;
          gap: 8px;
          justify-content: flex-start;
        }
        .ar-performance-stat-card__icon {
          align-items: center;
          background: #f8fafc;
          border-radius: 8px;
          display: inline-flex;
          flex: 0 0 auto;
          height: 28px;
          justify-content: center;
          width: 28px;
        }
        .ar-performance-stat-card .rp-stat-card__value-wrap {
          align-items: flex-start;
          flex: 1 1 auto;
          min-width: 0;
          text-align: left;
        }
        .ar-performance-stat-card .rp-stat-card__value {
          font-size: 20px;
          white-space: normal;
          word-break: break-word;
        }
        .ar-performance-stat-card .rp-stat-card__delta-wrap {
          align-items: flex-start;
          flex-direction: column;
          gap: 2px;
        }
        .ar-performance-split {
          align-items: stretch;
          display: grid;
          gap: 18px;
          grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
        }
        .ar-performance-chart {
          background: #fff;
          border: 1px solid #edf0f4;
          border-radius: 8px;
          min-height: 320px;
          padding: 12px 12px 4px;
        }
        .ar-perf-summary {
          min-height: auto;
        }
        .ar-summary-row {
          align-items: center;
          display: flex;
          gap: 16px;
          justify-content: space-between;
        }
        .ar-current-settlement-grid {
          display: grid;
          gap: 12px;
          grid-template-columns: repeat(5, minmax(0, 1fr));
        }
        .ar-settlement-metric-card {
          background: #fff;
          border: 1px solid #edf0f4;
          border-radius: 8px;
          min-width: 0;
          padding: 14px;
        }
        .ar-settlement-metric-card__top {
          align-items: center;
          display: flex;
          gap: 8px;
          margin-bottom: 8px;
        }
        .ar-settlement-metric-card__icon {
          color: #ec421c;
          display: inline-flex;
        }
        .ar-settlement-metric-card__value {
          color: #111827;
          font-size: 18px;
          font-weight: 700;
          overflow-wrap: anywhere;
        }
        .ar-settlement-metric-card__support {
          color: #6b7280;
          font-size: 12px;
          line-height: 1.5;
          margin-top: 8px;
        }
        .ar-invoice-cell {
          align-items: flex-end;
          display: inline-flex;
          flex-direction: column;
          gap: 4px;
        }
        .ar-inline-link {
          background: none;
          border: 0;
          color: #ec421c;
          cursor: pointer;
          font-size: 12px;
          font-weight: 700;
          padding: 0;
          text-decoration: underline;
        }
        .ar-compact-empty {
          margin: 0;
          padding: 24px;
          text-align: center;
        }
        .ar-hero-title-row {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 12px;
        }
        .ar-history-section .rp-table {
          height: auto !important;
          max-height: 420px !important;
        }
        @media (max-width: 1399px) {
          .ar-performance-card-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
          .ar-current-settlement-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        @media (max-width: 991px) {
          .ar-performance-header-action {
            align-items: flex-start;
            width: 100%;
          }
          .ar-performance-controls {
            justify-content: flex-start;
          }
          .ar-performance-split {
            grid-template-columns: 1fr;
          }
          .ar-current-settlement-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 575px) {
          .ar-performance-controls,
          .ar-period-toggle,
          .ar-date-range-picker,
          .ar-date-range-button {
            width: 100%;
          }
          .ar-period-toggle {
            justify-content: flex-start;
          }
          .ar-period-btn {
            flex: 1 1 auto;
          }
          .ar-date-range-popover {
            left: 0;
            right: auto;
            width: min(100%, 300px);
          }
          .ar-performance-card-grid,
          .ar-current-settlement-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <Container fluid>
        <div className="rp-page ar-page" style={{ borderRadius: 0 }}>
          {/* ---- Top bar ---- */}
          <div className="ar-topbar">
            <div className="ar-topbar__left">
              <p className="ar-breadcrumb">
                <span className="ar-breadcrumb__link" onClick={() => navigate("/admin/restaurants")}>Restaurants</span>
                <span className="ar-breadcrumb__sep">›</span>
                <span className="ar-breadcrumb__current">{restaurantData?.title || "Restaurant Detail"}</span>
              </p>
            </div>
            <div className="ar-topbar__actions">
              <SecondaryButton
                variant="light-grey"
                onClick={() => navigate(`/admin/restaurants/${restaurantId}/edit`, { state: { restaurantData } })}
              >
                Edit Restaurant
              </SecondaryButton>
              {nextAccountStatus && (
                <SecondaryButton
                  type="button"
                  variant={accountStatus === "active" ? "light-red" : "green"}
                  onClick={() => openAccountStatusModal(nextAccountStatus)}
                  disabled={updatingAccountStatus}
                  loading={updatingAccountStatus}
                >
                  {accountActionLabel}
                </SecondaryButton>
              )}
            </div>
          </div>

          {loadingRestaurant && !restaurantData ? (
            <div className="ar-loading">
              <div className="rp-skeleton" style={{ width: 260, height: 32, marginBottom: 20 }} />
              <div className="rp-skeleton" style={{ width: "100%", height: 220 }} />
            </div>
          ) : (
            <>
              {/* ---- Hero: banner + details + working hours ---- */}
              <div className="rp-card ar-hero-card">
                <div className="ar-hero-grid">
                  <div className="ar-hero-media">
                    <img
                      src={restaurantImageUrl || thumbPlaceholder}
                      alt={restaurantData?.title || "Restaurant"}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = thumbPlaceholder;
                      }}
                    />
                    <div className="ar-hero-logo">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <span>{(restaurantData?.title || "R").slice(0, 1).toUpperCase()}</span>
                      )}
                    </div>
                  </div>

                  <div className="ar-hero-details">
                    <div className="ar-hero-title-row" style={{ display: 'flex', alignItems: 'center' }}>
                      <h2 className="ar-hero-title" style={{ margin: 0, marginTop: '2px' }}>{restaurantData?.title}</h2>
                      <StatusBadge status={accountStatusLabel} />
                    </div>
                    <span className="ar-detail-item__label">Legal Business Name</span>
                    <span className="ar-detail-item__value">{restaurantData?.legal_business_name || "N/A"}</span>

                    <div className="ar-hero-row">
                      <div>
                        <span className="ar-detail-item__label">Categories</span>
                        <div className="ar-chip-row">
                          {categories.length ? categories.map((c) => (
                            <span className="ar-chip" key={c.id}>{c.title}</span>
                          )) : <span className="ar-detail-item__value">N/A</span>}
                        </div>
                      </div>
                      <div>
                        <span className="ar-detail-item__label">Price Range</span>
                        <span className="ar-detail-item__value">{restaurantData?.price_range || "N/A"}</span>
                      </div>
                    </div>

                    <span className="ar-detail-item__label">Description</span>
                    <p className="ar-hero-description">{restaurantData?.overview || "No description available."}</p>
                  </div>

                  <div className="ar-hero-hours">
                    <h3 className="ar-hero-hours__title">Working Hours</h3>
                    {workingHours.length ? (
                      <ul className="ar-hours-list">
                        {workingHours.map((row) => (
                          <li key={row.day} className="ar-hours-list__row">
                            <span className="ar-hours-list__day">{row.day}</span>
                            <span className="ar-hours-list__times">
                              {row.ranges.map((range) => (
                                <span key={range} className="ar-hours-list__time">{range}</span>
                              ))}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="ar-pending__text">Hours not set for this restaurant.</p>
                    )}
                  </div>
                </div>

                <div className="ar-hero-footer">
                  <div className="ar-address-grid">
                    <div style={{ gridColumn: "1 / -1" }}>
                      <span className="ar-detail-item__label">Address</span>
                      <span className="ar-detail-item__value">{address?.address || emptyValue}</span>
                    </div>
                    <div>
                      <span className="ar-detail-item__label">City</span>
                      <span className="ar-detail-item__value">{address?.city || emptyValue}</span>
                    </div>
                    <div>
                      <span className="ar-detail-item__label">State</span>
                      <span className="ar-detail-item__value">{address?.state || emptyValue}</span>
                    </div>
                    <div>
                      <span className="ar-detail-item__label">Country</span>
                      <span className="ar-detail-item__value">{address?.country || emptyValue}</span>
                    </div>
                    <div>
                      <span className="ar-detail-item__label">ZIP Code</span>
                      <span className="ar-detail-item__value">{address?.zipcode || emptyValue}</span>
                    </div>
                  </div>
                  {mapSrc ? (
                    <iframe
                      className="ar-map"
                      title="Restaurant location"
                      src={mapSrc}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  ) : (
                    <div className="ar-map ar-map--empty">No coordinates on file</div>
                  )}
                </div>
              </div>

              {/* ---- Owner Info + Linked Settlement Account ---- */}
              <div className="ar-two-col">
                <SectionCard title="Owner/Admin Information" subtitle="View restaurant owner and admin details">
                  <div className="ar-detail-grid">
                    <div className="ar-detail-item">
                      <span className="ar-detail-item__label">Owner Name</span>
                      <span className="ar-detail-item__value">{formatPrimaryOwnerName(primaryOwner)}</span>
                    </div>
                    <div className="ar-detail-item">
                      <span className="ar-detail-item__label">Email</span>
                      <span className="ar-detail-item__value">{formatPrimaryOwnerValue(primaryOwner?.email)}</span>
                    </div>
                    <div className="ar-detail-item">
                      <span className="ar-detail-item__label">Mobile</span>
                      <span className="ar-detail-item__value">{formatPrimaryOwnerValue(primaryOwner?.mobile)}</span>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title="Linked Settlement Account" subtitle="Manage settlement account details">
                  {loadingLinkedSettlementAccount ? (
                    <div className="ar-loading">
                      <div className="rp-skeleton" style={{ width: "100%", height: 96 }} />
                    </div>
                  ) : linkedSettlementAccount ? (
                    <>
                      {linkedSettlementAccountError && <p className="text-danger">{linkedSettlementAccountError}</p>}
                      <div className="ar-detail-grid">
                        {linkedSettlementAccountDetails.map((item) => (
                          <div className="ar-detail-item" key={item.label}>
                            <span className="ar-detail-item__label">{item.label}</span>
                            <span className="ar-detail-item__value">{item.value}</span>
                          </div>
                        ))}
                      </div>
                      <div className="ar-card-actions" style={{ marginTop: 16 }}>
                        <SecondaryButton
                          variant="light-red"
                          onClick={handleUnlinkSettlementAccountClick}
                          disabled={unlinkingSettlementAccount}
                        >
                          {unlinkingSettlementAccount ? "Unlinking..." : "Unlink"}
                        </SecondaryButton>
                      </div>
                    </>
                  ) : (
                    <div className="ar-pending">
                      {linkedSettlementAccountError && <p className="text-danger">{linkedSettlementAccountError}</p>}
                      <p className="ar-pending__text">No settlement account is linked to this restaurant.</p>
                    </div>
                  )}
                </SectionCard>
              </div>

              {/* ---- Restaurant Performance ---- */}
              <SectionCard
                title="Restaurant Performance"
                subtitle="View restaurant performance metrics and analytics"
                action={
                  <div className="ar-performance-header-action">
                    <div className="ar-performance-controls">
                      <div className="ar-period-toggle">
                        {Object.entries(PERFORMANCE_PERIODS).map(([p, config]) => (
                          <button
                            key={p}
                            type="button"
                            className={`ar-period-btn ${performancePeriod === p ? "ar-period-btn--active" : ""}`}
                            onClick={() => handlePerformancePeriodSelect(p)}
                          >
                            {config.label}
                          </button>
                        ))}
                      </div>
                      <div className="ar-date-range-picker">
                        <button
                          type="button"
                          className={`ar-date-range-button ${performancePeriod === "custom" ? "ar-date-range-button--active" : ""}`}
                          onClick={() => setShowCustomDatePicker((open) => !open)}
                          aria-expanded={showCustomDatePicker}
                        >
                          <CalendarIcon />
                          <span>{customDateRangeLabel}</span>
                        </button>
                        {showCustomDatePicker && (
                          <div className="ar-date-range-popover">
                            <div className="ar-date-range-fields">
                              <label>
                                <span>From date</span>
                                <input
                                  type="date"
                                  value={customDateFrom}
                                  onChange={(event) => {
                                    setCustomDateFrom(event.target.value);
                                    setCustomDateError("");
                                  }}
                                />
                              </label>
                              <label>
                                <span>To date</span>
                                <input
                                  type="date"
                                  value={customDateTo}
                                  onChange={(event) => {
                                    setCustomDateTo(event.target.value);
                                    setCustomDateError("");
                                  }}
                                />
                              </label>
                            </div>
                            {customDateError && <span className="ar-performance-range-error">{customDateError}</span>}
                            <div className="ar-date-range-actions">
                              <button type="button" className="ar-period-btn" onClick={handleCustomPerformanceReset}>
                                Reset
                              </button>
                              <button type="button" className="ar-period-btn ar-period-btn--active" onClick={handleCustomPerformanceApply}>
                                Apply
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                }
              >
                {loadingPerformance ? (
                  <div className="ar-loading">
                    <div className="rp-skeleton" style={{ width: "100%", height: 120 }} />
                  </div>
                ) : (
                  <>
                    {performanceError && <p className="text-danger">{performanceError}</p>}
                    <div className="ar-performance-card-grid">
                      {performanceCards.map((card) => (
                        <StatCard
                          key={card.key}
                          label={card.label}
                          value={card.value}
                          accent={card.accent}
                          comparison={card.comparison}
                          icon={card.icon}
                        />
                      ))}
                    </div>

                    <div className="ar-performance-split">
                      <MiniPerformanceGraph buckets={graphBuckets} />
                      <div className="ar-perf-summary">
                        <h4 className="ar-perf-summary__title">
                          Summary ({selectedPerformancePeriodLabel !== emptyValue
                            ? selectedPerformancePeriodLabel
                            : performancePeriodFallback})
                        </h4>
                        <p className="ar-pending__text" style={{ marginBottom: 12 }}>
                          Restaurant Net Revenue = Member Spend - Voucher Redemptions - Platform Fee
                        </p>
                        {PERIOD_SUMMARY_DEFS.map((item) => (
                          <div className="ar-summary-row" key={item.key}>
                            <span>{item.label}</span>
                            <span>{formatMoney(performanceData?.summary?.[item.key])}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </SectionCard>

              {/* ---- Current Settlement + History ---- */}
              <SectionCard title="Current Settlement" subtitle="View current settlement period details">
                {loadingPerformance ? (
                  <div className="ar-loading">
                    <div className="rp-skeleton" style={{ width: "100%", height: 96 }} />
                  </div>
                ) : (
                  <div className="ar-current-settlement-grid">
                    <SettlementMetricCard
                      label="Settlement Period"
                      value={currentSettlementPeriod}
                      icon={<CalendarIcon />}
                    />
                    <SettlementMetricCard
                      label="Total Member Spend"
                      value={formatMoney(currentSettlement.total_member_spend)}
                      icon={<SpendIcon />}
                    />
                    <SettlementMetricCard
                      label="Platform Fee"
                      value={formatMoney(currentSettlement.total_platform_fee)}
                      icon={<PercentIcon />}
                    >
                      <div>Pending: {formatMoney(currentSettlement.pending_platform_fee)}</div>
                      <div>Paid: {formatMoney(currentSettlement.paid_platform_fee)}</div>
                    </SettlementMetricCard>
                    <SettlementMetricCard
                      label="Status"
                      value={<StatusBadge status={currentSettlementStatus} />}
                      icon={<ActivityDotIcon />}
                    />
                    <SettlementMetricCard
                      label="Invoice"
                      value={renderInvoiceDisplay(
                        currentSettlement.invoice,
                        (invoiceId) => navigate(`/admin/settlements/invoices/${invoiceId}`)
                      )}
                      icon={<DocumentIcon />}
                    />
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Weekly Settlement History" subtitle="View historical settlement records" className="ar-history-section">
                {settlementInvoicesError && <p className="text-danger">{settlementInvoicesError}</p>}
                <TableScroll>
                  <DataTable
                    columns={settlementInvoiceColumns}
                    rows={settlementInvoiceRows}
                    getRowKey={(row) => row.id}
                    loading={loadingSettlementInvoices}
                    loadingMore={loadingSettlementInvoicesMore}
                    onLoadMore={handleLoadMoreSettlementInvoices}
                    hasMore={settlementInvoicesHasMore}
                    compact
                  />
                </TableScroll>
              </SectionCard>

              {/* ---- Bottom 4-up: Vouchers / Transactions / Top Customers / Points Ledger ---- */}
              <div className="ar-bottom-grid">
                <SectionCard title="Recent Voucher Redemptions" subtitle="View recent voucher redemption activity">
                  <TableScroll>
                    <DataTable
                      columns={voucherColumns}
                      rows={voucherRows}
                      getRowKey={(row) => row.id}
                      loading={loadingVouchers}
                      loadingMore={loadingVouchersMore}
                      onLoadMore={handleLoadMoreVouchers}
                      hasMore={vouchersHasMore}
                    />
                  </TableScroll>
                </SectionCard>

                <SectionCard title="Recent Transactions" subtitle="View recent transaction history">
                  {transactionsError && <p className="text-danger">{transactionsError}</p>}
                  {!loadingTransactions && transactionRows.length === 0 && !transactionsError ? (
                    <p className="ar-pending__text ar-compact-empty">No recent transactions found.</p>
                  ) : (
                    <TableScroll>
                      <DataTable
                        columns={transactionColumns}
                        rows={transactionRows}
                        getRowKey={(row) => row.id}
                        loading={loadingTransactions}
                        loadingMore={loadingTransactionsMore}
                        onLoadMore={handleLoadMoreTransactions}
                        hasMore={transactionsHasMore}
                        compact
                      />
                    </TableScroll>
                  )}
                </SectionCard>

                <SectionCard title="Top Customers (by Spend)" subtitle="View top spending customers">
                  <TableScroll>
                    <DataTable
                      columns={topCustomerColumns}
                      rows={topCustomerRows}
                      getRowKey={(row) => row.id}
                      loading={loadingCustomers}
                      loadingMore={loadingCustomersMore}
                      onLoadMore={handleLoadMoreCustomers}
                      hasMore={customersHasMore}
                    />
                  </TableScroll>
                </SectionCard>

                <SectionCard title="Points Ledger" subtitle="View points ledger transactions">
                  <TableScroll>
                    <DataTable
                      columns={pointsLedgerColumns}
                      rows={pointsLedgerRows}
                      getRowKey={(row) => row.id}
                      loading={loadingPointsLedger}
                      loadingMore={loadingPointsLedgerMore}
                      onLoadMore={handleLoadMorePointsLedger}
                      hasMore={pointsLedgerHasMore}
                    />
                  </TableScroll>
                </SectionCard>
              </div>

              {/* ---- Reviews & Rating ---- */}
              <SectionCard title="Reviews & Rating" subtitle="View customer reviews and ratings">
                <div className="ar-reviews-grid">
                  <div className="ar-rating-summary">
                    <span className="ar-rating-summary__label">Overall Rating</span>
                    <span className="ar-rating-summary__value">{reviewsData?.data?.[0]?.commentable?.resturant_rating?.rating ?? restaurantData?.resturant_rating?.rating ?? "N/A"}</span>
                    <span className="ar-rating-summary__count">{reviewsData?.total ?? restaurantData?.resturant_rating?.total ?? 0} review{(reviewsData?.total ?? restaurantData?.resturant_rating?.total ?? 0) === 1 ? "" : "s"}</span>
                    <div className="ar-rating-bars">
                      {ratingBreakdown.map((row) => (
                        <div className="ar-rating-bar-row" key={row.star}>
                          <span>{row.star} ★</span>
                          <div className="ar-rating-bar-track">
                            <div className="ar-rating-bar-fill" style={{ width: `${row.pct}%` }} />
                          </div>
                          <span>{row.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="ar-reviews-table">
                    <TableScroll>
                      <DataTable
                        columns={reviewColumns}
                        rows={reviewRows}
                        getRowKey={(row) => row.id}
                        loading={loadingReviews}
                        loadingMore={loadingReviewsMore}
                        onLoadMore={handleLoadMoreReviews}
                        hasMore={reviewsHasMore}
                      />
                    </TableScroll>
                  </div>
                </div>
              </SectionCard>
            </>
          )}
        </div>
      </Container>
      <DeleteConfirmationModal
        isOpen={showAccountStatusModal}
        title={accountConfirmTitle}
        message={accountConfirmMessage}
        onCancel={closeAccountStatusModal}
        onConfirm={confirmAccountStatusUpdate}
        confirmButtonText={updatingAccountStatus ? "Updating..." : accountConfirmText}
        confirmButtonDisabled={updatingAccountStatus}
        itemName="restaurant account"
      />
      <DeleteConfirmationModal
        isOpen={showUnlinkSettlementModal}
        title="Unlink Settlement Account"
        message="Are you sure you want to unlink this settlement account?"
        onCancel={closeUnlinkSettlementModal}
        onConfirm={confirmUnlinkSettlementAccount}
        confirmButtonText={unlinkingSettlementAccount ? "Unlinking..." : "Unlink"}
        confirmButtonDisabled={unlinkingSettlementAccount}
        itemName="settlement account"
      />
    </div>
  );
};

export default AdminRestaurantDetailPage;
